/**
 * 日時表示の共通フォーマッタ (ja-JP)。
 *
 * 各ページに散在していた formatDate / formatDateTime の重複定義を集約したもの。
 * いずれも不正値・null は fallback (既定 '—') を返す。
 * 用途に合わせて以下を使い分ける:
 *
 * - formatDate            … 2026/06/10
 * - formatDateTime        … 2026/06/10 14:30
 * - formatDateTimeSeconds … 2026/06/10 14:30:25 (監査ログなど秒が要る箇所)
 * - formatDateTimeShort   … 06/10 14:30 (年を省略したい密なテーブル・履歴)
 */

export type DateInput = string | number | Date | null | undefined;

function toValidDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** 2026/06/10 */
export function formatDate(value: DateInput, fallback = '—'): string {
  const d = toValidDate(value);
  if (!d) return fallback;
  return d.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/** 2026/06/10 14:30 */
export function formatDateTime(value: DateInput, fallback = '—'): string {
  const d = toValidDate(value);
  if (!d) return fallback;
  return d.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** 2026/06/10 14:30:25 */
export function formatDateTimeSeconds(value: DateInput, fallback = '—'): string {
  const d = toValidDate(value);
  if (!d) return fallback;
  return d.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** 06/10 14:30 (年なし) */
export function formatDateTimeShort(value: DateInput, fallback = '—'): string {
  const d = toValidDate(value);
  if (!d) return fallback;
  return d.toLocaleString('ja-JP', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// 数値・金額フォーマッタ
//
// org-admin のレポート系/管理系ページに重複していた実装を集約したもの。
// null / undefined は fallback (既定 '—') を返す。
// ---------------------------------------------------------------------------

/** 1,234 (3桁区切り) */
export function formatNumber(n: number | null | undefined, fallback = '—'): string {
  if (n === null || n === undefined) return fallback;
  return n.toLocaleString();
}

/** ¥1,234 (小数は四捨五入) */
export function formatJpy(n: number | null | undefined, fallback = '—'): string {
  if (n === null || n === undefined) return fallback;
  return `¥${Math.round(n).toLocaleString()}`;
}

/** ¥1.2M / ¥12k / ¥1.2k / ¥123 (グラフ軸など幅の狭い箇所用) */
export function formatJpyShort(n: number): string {
  if (n >= 1_000_000) return `¥${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `¥${(n / 1_000).toFixed(0)}k`;
  if (n >= 1_000) return `¥${(n / 1_000).toFixed(1)}k`;
  return `¥${Math.round(n)}`;
}

/** 0.1234 → 12.3% (比率を受け取る点に注意) */
export function formatPct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}
